import { useState } from "react";
import { motion } from "framer-motion";
import { Check, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BUDGETS = ["$2,500–$5,000", "$5,000–$10,000", "$10,000+"];
const HEADCOUNTS = ["Under 50", "50–150", "150–500", "500–1,000", "1,000+"];

const field = "w-full rounded-xl border border-[#AEE6D5]/25 bg-[#0A2E2C]/60 px-4 py-3.5 text-sm text-[#F8FAFC] placeholder-[#F8FAFC]/40 outline-none transition-colors focus:border-[#D4AF37]";

export const BookingEngine = () => {
  const [form, setForm] = useState({ name_org: "", contact: "", event_goal: "", event_location: "", venue: "", headcount: "", budget: "" });
  const [status, setStatus] = useState("idle"); // idle | sending | done | error

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name_org || !form.contact || !form.event_goal || !form.event_location || !form.headcount || !form.budget) return;
    setStatus("sending");
    try {
      const res = await fetch(`${API}/bookings`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("failed");
      setStatus("done");
    } catch {
      setStatus("error");
    }
  };

  return (
    <section id="booking" className="relative z-10 scroll-mt-8 overflow-x-hidden bg-[#0B3B3C] px-6 py-20 sm:px-10 lg:px-12 lg:py-32" data-testid="booking-engine">
      <div className="mx-auto grid max-w-6xl items-center gap-12 lg:grid-cols-2">
        {/* video reel */}
        <motion.div
          className="relative overflow-hidden rounded-3xl border border-[#AEE6D5]/20 shadow-2xl"
          initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, amount: 0.3 }} transition={{ duration: 0.8 }}
        >
          <video
            className="aspect-[4/5] w-full object-cover lg:aspect-square"
            src="/assets/drumband.mp4"
            poster="/assets/hero-casey-stage.jpg"
            muted loop autoPlay playsInline preload="none"
          />
          <div className="pointer-events-none absolute inset-0" style={{ background: "linear-gradient(180deg, transparent 55%, rgba(10,46,44,0.85) 100%)" }} />
          <div className="absolute bottom-6 left-6">
            <p className="font-mono text-[0.6rem] uppercase tracking-[0.22em] text-[#AEE6D5]">Live from the stage</p>
            <p className="mt-1 font-serif text-xl text-[#F8FAFC]">Presence. Authority. Rhythm.</p>
          </div>
        </motion.div>

        {/* intake form */}
        <motion.div
          initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, amount: 0.3 }} transition={{ duration: 0.8 }}
        >
          <p className="font-mono text-[0.7rem] uppercase tracking-[0.3em] text-[#D4AF37]">Keynotes · Workshops · Corporate wellbeing</p>
          <h2 className="mt-4 font-serif text-4xl font-light leading-tight tracking-tight text-[#F8FAFC] sm:text-5xl">
            Check availability <span className="italic text-[#AEE6D5]">&amp; rates.</span>
          </h2>
          <p className="mt-4 max-w-md text-sm leading-relaxed text-[#F8FAFC]/70">
            Book Casey Muze for mental health organizations, nonprofits, schools, conferences, and
            corporate team-building. Tell us about your event and goals — we&#39;ll follow up with
            availability, formats, and rates.
          </p>

          {status === "done" ? (
            <div className="mt-8 flex items-start gap-4 rounded-2xl border border-[#D4AF37]/40 bg-[#0A2E2C]/50 p-6" data-testid="booking-success">
              <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#D4AF37] text-[#0A2E2C]"><Check className="h-5 w-5" /></span>
              <div>
                <p className="font-serif text-xl text-[#F8FAFC]">Inquiry received.</p>
                <p className="mt-1 text-sm text-[#F8FAFC]/70">Casey&#39;s team will review your event and follow up with availability and rates shortly.</p>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-8 space-y-4" data-testid="booking-form">
              <input className={field} data-testid="booking-name" placeholder="Full name & organization" value={form.name_org} onChange={set("name_org")} required />
              <input className={field} data-testid="booking-contact" placeholder="Primary email & direct line" value={form.contact} onChange={set("contact")} required />
              <input className={field} data-testid="booking-goal" placeholder="Event date & what you're planning" value={form.event_goal} onChange={set("event_goal")} required />
              <div className="grid gap-4 sm:grid-cols-2">
                <input className={field} data-testid="booking-location" placeholder="Event location (City, State)" value={form.event_location} onChange={set("event_location")} required />
                <input className={field} data-testid="booking-venue" placeholder="Venue (name or type) — optional" value={form.venue} onChange={set("venue")} />
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <select className={`${field} ${form.headcount ? "" : "text-[#F8FAFC]/40"}`} data-testid="booking-headcount" value={form.headcount} onChange={set("headcount")} required>
                  <option value="" disabled>Expected attendance…</option>
                  {HEADCOUNTS.map((h) => <option key={h} value={h} className="text-[#0A2E2C]">{h} people</option>)}
                </select>
                <select className={`${field} ${form.budget ? "" : "text-[#F8FAFC]/40"}`} data-testid="booking-budget" value={form.budget} onChange={set("budget")} required>
                  <option value="" disabled>Confirm budget…</option>
                  {BUDGETS.map((b) => <option key={b} value={b} className="text-[#0A2E2C]">{b}</option>)}
                </select>
              </div>

              {status === "error" && (
                <p className="text-sm text-red-300" data-testid="booking-error">Something went wrong — please try again in a moment.</p>
              )}

              <button
                type="submit"
                disabled={status === "sending"}
                data-testid="booking-submit"
                data-cursor="org"
                className="mt-2 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[#0A2E2C] px-8 py-4 text-sm font-bold text-[#D4AF37] ring-1 ring-[#D4AF37]/60 transition-all hover:bg-[#D4AF37] hover:text-[#0A2E2C] disabled:opacity-60"
              >
                {status === "sending" ? <><Loader2 className="h-4 w-4 animate-spin" /> Sending…</> : "Check Speaking Availability & Rates"}
              </button>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
};
